/**
 * Extract the important credit card properties for inclusion in our __rhAnalytics object.
 * The function argument should be either a CreditCardSynopsisShape or a CreditCardShape.
 * Previously, under a different name, this function tried to create the former from the latter, but there was no
 * need for that; we only needed to create something suitable for an __rhAnalytics object.
 *
 * @param {CreditCardShape|CreditCardSynopsisShape} cardInfo
 * @param {string} cardInfo.id
 * @param {string} cardInfo.primaryId
 * @param {string} cardInfo.name
 * @param {string} cardInfo.slug
 * @param {string} cardInfo.bank
 * @param {string} cardInfo.network
 * @param {string} cardInfo.productType
 * @param {string} cardInfo.displayType
 * @param {string} cardInfo.cardImageURL
 * @param {number} cardInfo.rank
 * @param {bool} cardInfo.isMonetized
 * @param {bool} cardInfo.isSponsored
 * @param {bool} cardInfo.netSubsequentYearValue
 * @param {bool} cardInfo.signUpBonusDollars
 * @param {string} cardInfo.detailsURL
 * @param {string} cardInfo.applyRedirectURL
 * @param {number} cardInfo.purchaseInterest
 * @param {number} cardInfo.balanceTransferInterest
 * @param {number} cardInfo.cashAdvanceInterest
 * @param {string} cardInfo.approvalLikelihood
 * @param {number} cardInfo.approvalLikelihoodScore
 * @param {Object} cardInfo.giftCardOffer
 * @param {number} cardInfo.minimumDeposit
 * @param {number} cardInfo.annualFee
 * @param {string} cardInfo.bestUsedFor
 * @param {number} cardInfo.balanceTransferPromoInterest
 * @param {number} cardInfo.balanceTransferPromoMonths
 * @param {Object} cardInfo.cardTypes
 * @param {Object} cardInfo.rewardTypes
 * @param {string} cardInfo.userCategory
 * @param {Object} cardInfo.ratehubReview
 * @param {number} cardInfo.ratehubReview.rating
 * @returns {Object}
 */
export default function extractRhAnalyticsFromCreditCard(cardInfo) {
    return {
        id: cardInfo.id,
        primaryId: cardInfo.primaryId,
        name: cardInfo.name,
        slug: cardInfo.slug,
        bank: cardInfo.bank,
        network: cardInfo.network,

        productType: cardInfo.productType,
        displayType: cardInfo.displayType,

        cardImageURL: cardInfo.cardImageURL,

        rank: cardInfo.rank,

        isMonetized: cardInfo.isMonetized,
        isSponsored: cardInfo.isSponsored,

        netSubsequentYearValue: cardInfo.netSubsequentYearValue,
        signUpBonusDollars: cardInfo.signUpBonusDollars,

        detailsURL: cardInfo.detailsURL,
        applyRedirectURL: cardInfo.applyRedirectURL,

        purchaseInterest: cardInfo.purchaseInterest,
        balanceTransferInterest: cardInfo.balanceTransferInterest,
        cashAdvanceInterest: cardInfo.cashAdvanceInterest,

        approvalLikelihood: cardInfo.approvalLikelihood,
        approvalLikelihoodScore: cardInfo.approvalLikelihoodScore,

        giftCardOffer: cardInfo.giftCardOffer,

        minimumDeposit: cardInfo.minimumDeposit,
        annualFee: cardInfo.annualFee,
        bestUsedFor: cardInfo.bestUsedFor,

        balanceTransferPromoInterest: cardInfo.balanceTransferPromoInterest,
        balanceTransferPromoMonths: cardInfo.balanceTransferPromoMonths,

        cardTypes: cardInfo.cardTypes,

        // TODO: possible removal with CCDEP-2110 if analytics is not using these fields
        rewardTypes: cardInfo.rewardTypes,
        userCategory: cardInfo.userCategory,

        // for analytics, we only care about the `rating`, and leave ratehubReview undefined if there is no rating
        ...(cardInfo.ratehubReview && {
            ratehubReview: {
                rating: cardInfo.ratehubReview.rating,
            },
        }),
    };
}
